"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkAltiumRequiredFiles = checkAltiumRequiredFiles;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const altiumFileTypes_1 = require("./altiumFileTypes");
const altiumValidation_1 = require("./altiumValidation");
const altiumProjectFileUtil_1 = require("./parsers/project/altiumProjectFileUtil");
const altiumProjectFileTypes_1 = require("./parsers/project/altiumProjectFileTypes");
const project_detection_1 = require("../project-detection");
function getParsedProjectFile(detail) {
    if (detail.cadTool !== 'altium' || detail.semanticType !== 'project' || !detail.fileValidationResult?.isValid) {
        return undefined;
    }
    const value = detail.fileValidationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.Project];
    if (typeof value !== 'object' || value === null) {
        return undefined;
    }
    return value;
}
function getParsedStructureFile(detail) {
    if (detail.cadTool !== 'altium' || !detail.fileValidationResult?.isValid) {
        return undefined;
    }
    const value = detail.fileValidationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.ProjectStructure];
    if (typeof value !== 'object' || value === null) {
        return undefined;
    }
    return value;
}
function uploadHasSheetSymbols(fileDetails) {
    return fileDetails.some((detail) => detail.cadTool === 'altium' &&
        detail.semanticType === 'schematic' &&
        (detail.referencedFiles?.some((ref) => ref.trim() !== '') ?? false));
}
function checkProjectStructureRule(context) {
    if (context.files.some((f) => (0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.ProjectStructure)(f.relativePath))) {
        return [];
    }
    const hasSchematics = context.fileDetails.some((detail) => detail.cadTool === 'altium' && detail.semanticType === 'schematic');
    if (!hasSchematics) {
        return [];
    }
    const results = [];
    for (const detail of context.fileDetails) {
        const projectFile = getParsedProjectFile(detail);
        if (!projectFile) {
            continue;
        }
        const util = new altiumProjectFileUtil_1.AltiumProjectFileUtil(projectFile);
        const configured = util.getDesignConfiguration('HierarchyMode');
        const resolved = configured === altiumProjectFileTypes_1.HierarchyMode.Automatic && uploadHasSheetSymbols(context.fileDetails) ?
            altiumProjectFileTypes_1.HierarchyMode.Hierarchical
            : configured;
        if (!util.checkHierarchyModeRequiresProjectStructure(resolved)) {
            continue;
        }
        results.push({
            type: 'required-file-check',
            severity: sigma_engine_shared_1.BoundaryLevel.Error,
            message: `"${detail.relativePath}" is a hierarchical design (hierarchy mode ${altiumProjectFileTypes_1.HierarchyMode[resolved]}), which requires a ${altiumFileTypes_1.AltiumFileType.ProjectStructure} file that is not in the uploaded files`,
            rule: 'altium-project-structure',
            cadTool: 'altium',
            requiredFileExt: altiumFileTypes_1.AltiumFileType.ProjectStructure,
            triggeringFile: detail.relativePath,
            errorType: sigma_engine_shared_1.FileCollectionValidationErrorType.MissingRequiredFile,
        });
    }
    return results;
}
function normalizeSheetName(value) {
    return sigma_engine_shared_1.Files.basenamePath((0, project_detection_1.normalizePathForCompare)(value.trim())).trim();
}
function addSchematicName(target, value) {
    if (value === undefined) {
        return;
    }
    const normalized = normalizeSheetName(value);
    if (normalized === '' || !(0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.SchDoc)(normalized)) {
        return;
    }
    if (!target.has(normalized)) {
        target.set(normalized, sigma_engine_shared_1.Files.basenamePath((0, project_detection_1.toForwardSlash)(value.trim())).trim());
    }
}
function collectStructureSchematics(structure) {
    const sheets = new Map();
    for (const record of structure.topLevelDocuments ?? []) {
        addSchematicName(sheets, record.FILENAME);
    }
    for (const record of structure.sheetSymbols ?? []) {
        addSchematicName(sheets, record.FILENAME);
    }
    for (const record of structure.noMainPathDocuments ?? []) {
        addSchematicName(sheets, record.FILENAME);
    }
    return sheets;
}
function collectProjectSchematics(projectFile) {
    const sheets = new Map();
    for (const document of projectFile.Document ?? []) {
        addSchematicName(sheets, document.DocumentPath);
    }
    return sheets;
}
function findBoundProjectDetail(structurePath, fileDetails) {
    const structureDir = (0, project_detection_1.dirnameForward)(structurePath);
    let boundProject;
    let boundHomeDirLength = -1;
    for (const detail of fileDetails) {
        if (!(0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.Project)(detail.relativePath)) {
            continue;
        }
        const projectHomeDir = (0, project_detection_1.dirnameForward)(detail.relativePath);
        if ((0, project_detection_1.isSameOrDescendantDir)(structureDir, projectHomeDir) && projectHomeDir.length > boundHomeDirLength) {
            boundProject = detail;
            boundHomeDirLength = projectHomeDir.length;
        }
    }
    return boundProject;
}
function hasMismatchedProjectStem(structurePath, projectPath) {
    return (0, project_detection_1.basenameWithoutExtension)(structurePath).toLowerCase() !== (0, project_detection_1.basenameWithoutExtension)(projectPath).toLowerCase();
}
function mismatchedProjectStemDiagnosis(structurePath, projectPath) {
    return `"${structurePath}" appears to belong to a different project or an older snapshot than "${projectPath}". REMOVE the stale ${altiumFileTypes_1.AltiumFileType.ProjectStructure} file, rename it to match the project, or re-export the project so Altium regenerates it`;
}
function checkStructureConsistencyRule(context) {
    const hasSchematics = context.fileDetails.some((detail) => detail.cadTool === 'altium' && detail.semanticType === 'schematic');
    if (!hasSchematics) {
        return [];
    }
    const results = [];
    for (const detail of context.fileDetails) {
        if (!(0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.ProjectStructure)(detail.relativePath)) {
            continue;
        }
        const structure = getParsedStructureFile(detail);
        if (!structure) {
            continue;
        }
        const projectDetail = findBoundProjectDetail(detail.relativePath, context.fileDetails);
        if (!projectDetail) {
            results.push({
                type: 'structure-consistency-check',
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                message: `"${detail.relativePath}" belongs to a project that is not in the upload; remove it, or upload its project`,
                rule: 'altium-structure-consistency',
                cadTool: 'altium',
                structureFile: detail.relativePath,
                attributedFilePath: detail.relativePath,
                missingFromStructure: [],
                unknownToProject: [],
                errorType: sigma_engine_shared_1.FileCollectionValidationErrorType.UnpairedStructureFile,
            });
            continue;
        }
        const projectFile = getParsedProjectFile(projectDetail);
        if (!projectFile) {
            continue;
        }
        const projectSheets = collectProjectSchematics(projectFile);
        const structureSheets = collectStructureSchematics(structure);
        const missingFromStructure = [...projectSheets]
            .filter(([normalized]) => !structureSheets.has(normalized))
            .map(([, displayName]) => displayName);
        const unknownToProject = [...structureSheets]
            .filter(([normalized]) => !projectSheets.has(normalized))
            .map(([, displayName]) => displayName);
        const nameMismatch = hasMismatchedProjectStem(detail.relativePath, projectDetail.relativePath);
        if (missingFromStructure.length === 0 && unknownToProject.length === 0 && !nameMismatch) {
            continue;
        }
        let severity;
        let message;
        if (missingFromStructure.length > 0) {
            const problems = [
                `the project lists schematic sheets the structure file does not name (${missingFromStructure.join(', ')})`,
            ];
            if (unknownToProject.length > 0) {
                problems.push(`the structure file names schematic sheets the project does not list (${unknownToProject.join(', ')})`);
            }
            severity = sigma_engine_shared_1.BoundaryLevel.Error;
            message = `"${detail.relativePath}" is out of sync with "${projectDetail.relativePath}": ${problems.join('; ')}. Re-save or re-export the project in Altium so the ${altiumFileTypes_1.AltiumFileType.ProjectStructure} file is regenerated in sync with the project`;
        }
        else {
            severity = sigma_engine_shared_1.BoundaryLevel.Warning;
            message =
                unknownToProject.length > 0 ?
                    `"${detail.relativePath}" names schematic sheets that "${projectDetail.relativePath}" does not list (${unknownToProject.join(', ')}). These may be device sheets or managed sheets not listed in the project file; verify the ${altiumFileTypes_1.AltiumFileType.ProjectStructure} file matches the uploaded project`
                    : `"${detail.relativePath}" otherwise matches "${projectDetail.relativePath}"`;
        }
        if (nameMismatch) {
            message = `${message}. ${mismatchedProjectStemDiagnosis(detail.relativePath, projectDetail.relativePath)}`;
        }
        results.push({
            type: 'structure-consistency-check',
            severity,
            message,
            rule: 'altium-structure-consistency',
            cadTool: 'altium',
            structureFile: detail.relativePath,
            attributedFilePath: detail.relativePath,
            projectFile: projectDetail.relativePath,
            missingFromStructure,
            unknownToProject,
            errorType: sigma_engine_shared_1.FileCollectionValidationErrorType.InconsistentStructureFile,
        });
    }
    return results;
}
function checkAnnotationRule(context) {
    if (context.missingReferencedFiles.length === 0) {
        return [];
    }
    const missing = new Set(context.missingReferencedFiles);
    const results = [];
    for (const detail of context.fileDetails) {
        if (!getParsedProjectFile(detail)) {
            continue;
        }
        for (const ref of detail.referencedFiles ?? []) {
            if (!(0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.Annotation)(ref) || !missing.has(ref)) {
                continue;
            }
            results.push({
                type: 'required-file-check',
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                message: `"${detail.relativePath}" was re-annotated using "${ref}", which is not in the uploaded files`,
                rule: 'altium-annotation',
                cadTool: 'altium',
                requiredFileExt: altiumFileTypes_1.AltiumFileType.Annotation,
                triggeringFile: detail.relativePath,
                errorType: sigma_engine_shared_1.FileCollectionValidationErrorType.MissingRequiredFile,
            });
        }
    }
    return results;
}
function checkAltiumRequiredFiles(context) {
    return [
        ...checkProjectStructureRule(context),
        ...checkStructureConsistencyRule(context),
        ...checkAnnotationRule(context),
    ];
}
//# sourceMappingURL=altiumRequiredFiles.js.map